import type {FieldError} from 'react-hook-form';

import {isNil, isNotEmpty, isNotNil, uniq} from 'ramda';

/**
 * Shape of `fieldState.error` for numeric bound fields (nullable object with optional `from`/`to`).
 * Slightly wider than `FieldError` so it accepts RHF's `Merge<FieldError, FieldErrorsImpl<…>>` (e.g. optional `type`).
 */
export type NumericBoundFieldErrorState = {
  type?: FieldError['type'];
  message?: FieldError['message'];
  ref?: FieldError['ref'];
  from?: FieldError;
  to?: FieldError;
};

/** User-visible text for a numeric bound field error: root `message`, or deduplicated `from` / `to` messages joined by newlines. */
export function mergeNumericBoundFieldError(
  error: NumericBoundFieldErrorState | undefined
): string | undefined {
  if (isNil(error) || typeof error !== 'object') {
    return undefined;
  }

  const field = error;

  if (typeof field.message === 'string' && isNotEmpty(field.message)) {
    return field.message;
  }

  const parts = [field.from?.message, field.to?.message].filter(
    (m): m is string => isNotNil(m) && isNotEmpty(m)
  );

  const uniqueParts = uniq(parts);

  return uniqueParts.length > 0 ? uniqueParts.join('\n') : undefined;
}

function hasNestedMessage(node: unknown): boolean {
  if (isNil(node) || typeof node !== 'object') {
    return false;
  }
  const message = Reflect.get(node, 'message');
  return typeof message === 'string' && isNotEmpty(message);
}

/** Which magnitude inputs (offset mode) should show invalid styling for a numeric bound error. */
export function getNumericBoundMagnitudeInvalidEnds(
  error: NumericBoundFieldErrorState | undefined
): {
  isFromMagnitudeInvalid: boolean;
  isToMagnitudeInvalid: boolean;
} {
  if (isNil(error) || typeof error !== 'object') {
    return {isFromMagnitudeInvalid: false, isToMagnitudeInvalid: false};
  }

  const e = error;

  return {
    isFromMagnitudeInvalid: hasNestedMessage(e.from),
    isToMagnitudeInvalid: hasNestedMessage(e.to),
  };
}
